import * as angular from 'angular';
import { find, isEmpty, isEqual } from 'lodash';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import { QueryOptions, RecordNames } from '@mode/shared/data-access-algolia';
import { BugsnagHelpers } from '~/lib/bugsnag-helpers';
import { SearchClientService } from '@mode/algolia/domain';

// keycodes for the "arrow keys" and the "enter" keys
const NAVIGATIONKEYCODES = [37, 38, 39, 40, 313];

export class SearchSelectorController {
  public selectedList: any[] = []; // selected hits from AlgoliaQueryResult
  public searchList: any[] = []; // hits from AlgoliaQueryResult
  public queryString: '';
  private queryString$: BehaviorSubject<string> = new BehaviorSubject(null);
  public algoliaConfig: QueryOptions<RecordNames>; // any of the configuration for the record types from search service
  public algoliaConfig$: BehaviorSubject<QueryOptions<RecordNames>> = new BehaviorSubject(null);
  public algoliaConfigWithQuery$: Observable<QueryOptions<RecordNames>>;

  public onCancel: () => void;
  public onConfirm: ($selectedList: any) => void;

  static $inject = ['SearchClientService'];

  constructor(private _searchClientService: SearchClientService) {
    // filter out the nulls from queryString$ and algoliaConfig$
    const queries$ = this.queryString$.pipe(filter((queryString) => queryString != null));
    const config$ = this.algoliaConfig$.pipe(filter((algoliaConfig) => algoliaConfig != null));

    // once queries$ and config$ are defined, create a algoliaConfigWithQuery$ observable
    this.algoliaConfigWithQuery$ = combineLatest(queries$, config$, (queryString, config) => ({
      ...config,
      query: queryString,
    }));
    // and listen to that observable, to send a query to algolia
    this.algoliaConfigWithQuery$.subscribe((configWithQuery) => {
      this.querySearchService(configWithQuery);
    });
  }

  $onChanges(changed: angular.IOnChangesObject) {
    const { algoliaConfig } = changed;
    // when you recieve an algoliaConfig
    if (algoliaConfig != null && isEmpty(algoliaConfig.previousValue) && algoliaConfig.currentValue) {
      // make a initial query with that config
      this.algoliaConfig$.next(algoliaConfig.currentValue);
      this.sendInitalQuery();
    }
  }

  sendInitalQuery() {
    // set the queryString$ and kickstart the observable chain, to send the first query
    this.queryString$.next('');
  }

  querySearchService(configWithQuery) {
    // make a promise via the search service using the updated algolia config
    const queryPromise = this._searchClientService.query(configWithQuery);
    // then handle the success or failure of that promise
    return queryPromise
      .then((response) => this.handleAlgoliaSearchResponse(response))
      .catch((err) => {
        BugsnagHelpers.notifySafely(err);
        console.error(err);
      });
  }

  handleAlgoliaSearchResponse(algoliaResponse) {
    let hitsList = [];
    algoliaResponse.results.forEach((result) => {
      hitsList = hitsList.concat(result.hits);
    });
    this.searchList = hitsList;
  }

  updateQueryString(newQueryString) {
    // this.queryString = newQuery;
    this.queryString$.next(newQueryString);
    // this.querySearchService();
  }

  findSelectedItem(selectedItem) {
    return find(this.selectedList, {
      token: selectedItem.token,
      objectType: selectedItem.objectType,
    });
  }

  handleOnSelect(selectedItem) {
    // when a user selects an item, if it isn't already in the list
    if (!this.findSelectedItem(selectedItem)) {
      // push it into the selectedList
      this.selectedList.push(selectedItem);
    }
    // and reset the queryString for the ui-select
    this.queryString = '';
    // and reset the associated observable
    this.queryString$.next('');
    // Remove the focus from the UI select
    this.clearSeachInputFocus();
  }

  clearSeachInputFocus() {
    setTimeout(function () {
      $(':focus').blur();
    });
  }

  handleRemoveSelectedItem(selectedItem) {
    const newList = this.selectedList.filter((item) => !isEqual(item, selectedItem));
    this.selectedList = newList;
  }

  handleKeyUp(event, data) {
    // only re-query algolia if they keystroke are not
    // from the user navigating with the arrow keys || enter
    if (!NAVIGATIONKEYCODES.includes(event.keyCode)) {
      this.updateQueryString(data);
    }
  }

  handleCancelClick() {
    // invoke the cancel method given by parent
    this.onCancel();
  }

  handleConfirmClick() {
    // invoke the confirm method given by parent
    const $selectedList = this.selectedList;
    this.onConfirm({ $selectedList });
  }
}

export const SearchSelectorComponent: angular.IComponentOptions = {
  controller: SearchSelectorController,
  controllerAs: 'ctrl',
  bindings: {
    algoliaConfig: '<',
    emptySelectedStateText: '@',
    onConfirm: '&',
    confirmButtonText: '@',
    onCancel: '&',
    cancelButtonText: '@',
  },
  template: `
    <!-- Start Selection Screen -->
    <div class="selection-view">
      <ui-select
        ng-model="ctrl.queryString"
        theme="bootstrap"
        on-select="ctrl.handleOnSelect($item)"
        uis-open-close="ctrl.clearSeachInputFocus()"
        ng-keyup="ctrl.handleKeyUp($event, $select.search)"
        data-test="search-input"
      >
        <ui-select-match class="icon-search-spyglass" placeholder="Search...">
          {{ ctrl.queryString }}
        </ui-select-match>
        <ui-select-choices repeat="searchItem in ctrl.searchList">
          <div class="search-item" data-test="search-result"
            ng-class="{'selected-search-item': ctrl.findSelectedItem(searchItem) }"
          >
            <search-selector-item
              item="searchItem"
            ></search-selector-item>
          </div>

        </ui-select-choices>
        <ui-select-no-choice>
          No matches found.
        </ui-select-no-choice>
      </ui-select>

      <div ng-if="ctrl.selectedList.length === 0" class="no-selected-items">
        <strong>{{ ctrl.emptySelectedStateText }}</strong>
      </div>

      <div ng-if="ctrl.selectedList.length > 0" class="selected-items-listed">
        <div ng-repeat="selectedItem in ctrl.selectedList">
          <div class="selected-item">
            <search-selector-item
              item="selectedItem"
            ></search-selector-item>
            <span
              class="icon-action-close-default"
              ng-click="ctrl.handleRemoveSelectedItem(selectedItem)"
            ></span>
          </div>
        </div>
      </div>
    </div>
    <!-- END Selection Screen -->

    <!-- Start Control (Confirmation & Cancel) Buttons -->
    <div class="buttons-view">
      <capra-button
        class="action-button cancel"
        (clicked)="ctrl.handleCancelClick()"
        [text]="ctrl.cancelButtonText || 'Cancel'"
        variant="secondary"
        color="neutral"
      ></capra-button>
      <capra-button
        class="action-button"
        [disabled]="ctrl.selectedList.length === 0"
        (clicked)="ctrl.handleConfirmClick()"
        data-test="confirm-button"
        [text]="ctrl.confirmButtonText || 'Confirm'"
      ></capra-button>
    </div>
    <!-- END Control (Confirmation & Cancel) Buttons -->
`,
};
